import { authExchange } from '@urql/exchange-auth';
import { requestPolicyExchange } from '@urql/exchange-request-policy';
import { retryExchange } from '@urql/exchange-retry';
import { Kind } from 'graphql';
import get from 'lodash/get';
import { type PropsWithChildren, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  type CombinedError,
  cacheExchange,
  createClient,
  fetchExchange,
  mapExchange,
  type Operation,
  Provider,
} from 'urql';

import { fullLogout, useAuthStore } from '@revelio/auth';
import { isLocalhost } from '@revelio/core';
import { isNonRetriableError } from '@revelio/data-access';
import { getOperationRequestId, type KibanaLogger } from '@revelio/iso-utility';

import { environment } from '../../environments/environment';
import {
  recordGqlErrorEvent,
  recordTruncatedGraphQL,
} from '../../hooks/open-replay';
import { showAlertDialog } from '../components/alert-dialog';
import { dataInspectorExchange } from '../data-inspector/data-inspector-exchange';
import { useKibanaLogger } from '../hooks/kibana-logger/kibana-logger';
import { UrqlAndTanstackErrorMessage } from '../ui/urql-and-tanstack-error-message/urql-and-tanstack-error-message';
import {
  cacheBypassExchange,
  getCacheBypassFlags,
} from './cache-bypass-exchange';
import { priorityExchange } from './priority-exchange';
import {
  getTopNOverride,
  topNOverrideExchange,
} from './top-n-override-exchange';

// Register custom context types for urql operations
declare module 'urql' {
  interface OperationContext {
    skipGlobalErrorHandling?: boolean;
    priority?: 'high' | 'low';
    _startTime?: number;
    /** Chart card that issued this query; read by the Data Inspector. */
    chartItemId?: string;
  }
}

export const activeRequests = new Set<string>();

// Max urql retry attempts; surfaced in the retry log message so the support
// tooling can distinguish a non-final retry from the final hard failure.
const MAX_RETRY_ATTEMPTS = 2;

/**
 * Extract operation name from GraphQL operation
 */
const getOperationName = (operation: Operation): string => {
  return (
    operation?.query?.definitions?.find(
      (def) => def?.kind === Kind.OPERATION_DEFINITION
    )?.name?.value || String(operation.key)
  );
};

const logGqlError = (
  error: CombinedError,
  operation: Operation,
  kibanaLogger: KibanaLogger,
  userFacing: boolean
) => {
  const { message, logOptions, isWarningLevel, errorType } =
    kibanaLogger.getUrqlErrorLog(error, operation);
  const options = { ...logOptions, user_facing: userFacing, errorType };

  if (isWarningLevel) {
    console.warn(message);
    kibanaLogger.warn(message, options);
  } else {
    console.error(message);
    kibanaLogger.error(message, options);
    recordGqlErrorEvent(getOperationName(operation), error.message);
  }
};

const skipErrorDialogMessages = [
  // talent discovery raises this when paginating past the last page
  'no user ids',
  'seat limit has been reached',
  '[Network] The user aborted a request.',
];

const shouldDisplayErrorDialog = (
  error: CombinedError,
  operation: Operation,
  hideErrorDialog: boolean
): boolean => {
  if (
    hideErrorDialog ||
    skipErrorDialogMessages.some((msg) => error.message.includes(msg))
  ) {
    return false;
  }

  // Temporary error skipping for data builder column configuration getPipelineConfigs query during backend permission error (for non revelio admins)
  // since it shouldn't be blocking any users from finishing their deliverable configuration
  if (
    // biome-ignore lint/suspicious/noExplicitAny: migrated from develop
    (operation.query.definitions[0] as any).name.value === 'getPipelineConfigs'
  ) {
    return false;
  }

  return true;
};

const displayErrorDialog = (error: CombinedError, operation: Operation) => {
  const reqIdSearchResult = /(?:req-id:\s)(.*?)(?=,)/.exec(error.message);
  // Fall back to the operation header so the dialog always shows a copyable
  // request-id the user can quote in a support ticket.
  const reqId =
    get(reqIdSearchResult, '[1]') ?? getOperationRequestId(operation);

  showAlertDialog({
    alertType: 'actionless',
    isOpen: true,
    headerText: 'Sorry!',
    bodyContent: <UrqlAndTanstackErrorMessage error={error} reqId={reqId} />,
  });
};

/*
 * Track requests in progress
 */
const requestTrackerExchange = mapExchange({
  onOperation(operation) {
    const operationName = getOperationName(operation);
    if (['query', 'mutation', 'subscription'].includes(operation.kind)) {
      activeRequests.add(operationName);
    }

    if (operation.kind === 'teardown' && activeRequests.has(operationName)) {
      activeRequests.delete(operationName);
    }

    return operation;
  },
  onResult(result) {
    if (['query', 'mutation'].includes(result.operation.kind)) {
      const operationName = getOperationName(result.operation);
      if (activeRequests.has(operationName)) {
        activeRequests.delete(operationName);
      }
    }

    return result;
  },
});

/**
 * Stamps _startTime immediately before the fetchExchange to reflect the network round-trip.
 */
const timingExchange = mapExchange({
  onOperation(operation) {
    return {
      ...operation,
      context: {
        ...operation.context,
        _startTime: performance.now(),
      },
    };
  },
});

/**
 * Observes each network-level operation once (after cache deduplication) for Kibana, error dialogs, and OpenReplay.
 * Placed after cacheExchange so logging is not multiplied by useQuery subscriber count.
 */
const makeNetworkObserverExchange = (
  kibanaLogger: KibanaLogger | undefined,
  hideErrorDialogs: boolean
) =>
  mapExchange({
    onError(error, operation) {
      if (
        !operation.context.skipGlobalErrorHandling &&
        kibanaLogger !== undefined
      ) {
        const userFacing = shouldDisplayErrorDialog(
          error,
          operation,
          hideErrorDialogs
        );
        logGqlError(error, operation, kibanaLogger, userFacing);
        if (userFacing) displayErrorDialog(error, operation);
      }

      if (!operation.context.skipGlobalErrorHandling) {
        const startTime = operation.context._startTime;
        if (startTime !== undefined) {
          const duration = performance.now() - startTime;

          recordTruncatedGraphQL(
            operation.kind,
            getOperationName(operation),
            operation.variables,
            { error: error.message },
            duration
          );
        }
      }
    },
    onResult(result) {
      if (result.stale) {
        return result;
      }

      if (!['query', 'mutation'].includes(result.operation.kind)) {
        return result;
      }

      const startTime = result.operation.context._startTime;
      if (startTime === undefined) {
        return result;
      }

      const duration = performance.now() - startTime;

      recordTruncatedGraphQL(
        result.operation.kind,
        getOperationName(result.operation),
        result.operation.variables,
        result.data,
        duration
      );

      return result;
    },
  });

/** ================================
 * UrqlProvider
 ================================ */
const UrqlProvider = ({
  children,
  hideErrorDialogs = false,
}: PropsWithChildren<{
  hideErrorDialogs?: boolean;
}>) => {
  const { kibanaLogger } = useKibanaLogger();

  const urqlClient = useMemo(() => {
    return createClient({
      url: `${environment.GO_API_ROOT}/query`,
      preferGetMethod: false,
      fetchOptions: {
        credentials: 'include',
      },
      exchanges: [
        requestPolicyExchange({}),
        // ABOVE `cacheExchange` on purpose, unlike the Kibana observer below it.
        // `cacheExchange` answers a cache hit from its own `cachedOps$` branch
        // and filters that operation out of the stream it forwards, so anything
        // downstream is blind to it. Telemetry wants exactly that (log network
        // round-trips); the inspector wants the opposite — it explains the data
        // behind a chart wherever it came from, and "served from cache" is the
        // case it most needs to show. Placed here it also sees cached results
        // flow back, which is what makes the `fromCache` badge real.
        dataInspectorExchange,
        cacheExchange,
        makeNetworkObserverExchange(kibanaLogger, hideErrorDialogs),
        requestTrackerExchange,
        authExchange(async ({ appendHeaders }) => {
          return {
            addAuthToOperation: (operation) => {
              const auth = useAuthStore.getState();
              const xBenchmarkId: 'e2e-tests' | undefined = (() => {
                if (
                  !isLocalhost() &&
                  auth?.ory?.username === 'testing.framework@reveliolabs.com'
                ) {
                  return 'e2e-tests';
                }

                return undefined;
              })();

              return appendHeaders(operation, {
                'x-request-id': crypto.randomUUID(),
                ...(xBenchmarkId && { 'X-Benchmark-Id': xBenchmarkId }),
              });
            },
            didAuthError: (error) => {
              const status = error.response?.status;
              if (status === 401 || status === 403) return true;
              else return false;
            },
            refreshAuth: async () => {
              fullLogout();
            },
          };
        }),
        retryExchange({
          initialDelayMs: 1000,
          maxDelayMs: 5000,
          maxNumberAttempts: MAX_RETRY_ATTEMPTS,
          retryIf(error, operation) {
            if (isNonRetriableError(error)) return false;
            else {
              if (kibanaLogger && !operation.context.skipGlobalErrorHandling) {
                const { message, logOptions, isWarningLevel, errorType } =
                  kibanaLogger.getUrqlErrorLog(error, operation);

                const errorOptions = { ...logOptions, errorType };

                const retryAttempt = (operation.context?.retry?.count ?? 0) + 1;

                const messageWithRetry = `Retrying failed request (Attempt ${retryAttempt}/${MAX_RETRY_ATTEMPTS})... ${message}`;
                // Don't send a log here on final retry. It will get caught and sent in the regular error handler
                const sendErrorLog = retryAttempt < MAX_RETRY_ATTEMPTS;
                if (sendErrorLog) {
                  if (isWarningLevel) {
                    console.warn(messageWithRetry);
                    kibanaLogger.warn(messageWithRetry, errorOptions);
                  } else {
                    console.error(messageWithRetry);
                    kibanaLogger.error(messageWithRetry, errorOptions);
                  }
                }
              }

              return true;
            }
          },
        }),
        priorityExchange,
        cacheBypassExchange(getCacheBypassFlags),
        topNOverrideExchange(getTopNOverride),
        timingExchange,
        fetchExchange,
      ],
    });
  }, [kibanaLogger, hideErrorDialogs]);

  return <Provider value={urqlClient}>{children}</Provider>;
};

/** Route-aware UrqlProvider that handles error dialog visibility based on current route */
export const RouteAwareUrqlProvider = ({ children }: PropsWithChildren) => {
  const location = useLocation();
  const hideErrorDialogs = location.pathname === '/health-check';

  return (
    <UrqlProvider hideErrorDialogs={hideErrorDialogs}>{children}</UrqlProvider>
  );
};
